import classnames from "classnames";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

import { InquiryForm } from "@/components/Form/index";
import InquiryMetaTags from "@/components/MetaTags/InquiryMetaTags";
import Steps from "@/components/Steps";
import {
  useAuthUserQuery,
  useDeviceInfoQuery,
  useStep,
  useTermsQuery,
  useInquiryMutation,
} from "@/hooks";
import { inquiryList } from "@/lib/constants";
import getRemoteIpAddress from "@/lib/getRemoteIpAddress";
import routes from "@/lib/routes";
import processStyles from "@/styles/Process.module.scss";

export default function InquiryStep({ gip }) {
  const router = useRouter();
  const {
    data: { user },
  } = useAuthUserQuery();

  const [inquiry] = useInquiryMutation();

  const { step, state, dispatch } = useStep({}, { completeStep: 3 });

  const pageBack = useCallback(
    (e) => {
      e.preventDefault();
      dispatch({ type: "prev" });
    },
    [dispatch]
  );

  const handleStep1 = useCallback(
    async (data) => {
      return new Promise((resolve) => {
        dispatch({ type: "next", formData: data });
        resolve();
      });
    },
    [dispatch]
  );

  const handleStep2 = useCallback(
    //お問合せ内容送信処理
    async (data) => {
      return new Promise((resolve, reject) => {
        try {
          const params = {
            name: data?.name || user?.fullName,
            mailAddress: data?.mailAddress,
            membersId: data?.membersId || user?.mailAddress || "",
            pastSpooxId: data?.pastSpooxId || "",
            subject: inquiryList?.[data?.commentOption],
            body: data?.comment,
            comment: `UA:${data?.ua}\nGIP:${data?.gip}`,
          };
          try {
            inquiry({
              variables: params,
            });
          } catch (e) {
            throw e;
          }
          //次のstepへ
          dispatch({ type: "next" });
          resolve();
        } catch (e) {
          reject();
        }
      });
    },
    [user, dispatch, inquiry]
  );

  const { data: termsData } = useTermsQuery({
    variables: {
      keys: ["skp_privacypolicy"],
      withAgreement: true,
    },
  });
  const privacypolicy = useMemo(() => {
    if (termsData) {
      return {
        display_name: termsData?.terms?.[0]?.display_name,
        ...termsData?.terms?.[0]?.current_revision,
      };
    } else {
      return null;
    }
  }, [termsData]);

  useEffect(() => {
    //stepの URL更新
    if (step !== state.nextStep) {
      router.push(routes.inquiryStep(state.nextStep), undefined, {
        shallow: true,
        scroll: true,
      });
    }
  }, [router, step, state.nextStep]);

  const { data: { deviceInfo } = {} } = useDeviceInfoQuery({
    fetchPolicy: "cache-first",
  });

  const action = useMemo(() => {
    if (step === 1) return "index";
    if (step === 2) return "confirm";
    if (step === 3) return "complete";
  }, [step]);

  return (
    <>
      <InquiryMetaTags action={action} />
      <div
        className={classnames(processStyles.container, {
          [processStyles.complete]: step === 3,
        })}
      >
        <div className={processStyles.inner}>
          {step !== 3 && (
            <>
              <h2 className={processStyles.heading}>お問い合わせ</h2>
              <Steps
                step={step}
                steps={[
                  "お問い合わせ内容入力",
                  "入力内容の確認",
                  "お問い合わせ完了",
                ]}
              />
            </>
          )}
          <div>
            {step === 1 && (
              <InquiryForm.Step1
                onSubmit={handleStep1}
                user={user}
                formData={state.formData}
                privacypolicy={privacypolicy}
              />
            )}
            {step === 2 && (
              <InquiryForm.Step2
                onSubmit={handleStep2}
                pageBack={pageBack}
                formData={state.formData}
                user={user}
                privacypolicy={privacypolicy}
                gip={gip}
                ua={deviceInfo?.ua}
              />
            )}
            {step === 3 && <InquiryForm.Step3 />}
          </div>
        </div>
      </div>
    </>
  );
}
InquiryStep.layout = "NoHeaderLayout";

export async function getServerSideProps({ req }) {
  const gip = getRemoteIpAddress(req);
  return { props: { gip } };
}
